import { v4 as uuid } from 'uuid';
import {
  AreaConnection,
  HighwayArea,
  InterAreaConnection,
  RackingArea,
  Segment,
  SingularStationArea,
  StationaryArea,
} from '@thive/fddm-types';
import { assertNever } from '@/helpers/utils';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { Area } from '@/modules/floorplanDesign/types';

/**
 * The connectNeighbors field is present on all non-manual area types and shares the same union.
 * This alias avoids repeating `RackingArea['connectNeighbors']` everywhere.
 */
export type ConnectNeighbors =
  | HighwayArea['connectNeighbors']
  | RackingArea['connectNeighbors']
  | SingularStationArea['connectNeighbors']
  | StationaryArea['connectNeighbors'];

//
// Constants
//
/**
 * Returns the stored smooth heading preference, used as the default for new connections.
 */
export const isAnyVehicleOmni = (): boolean =>
  useFloorplanDesignStore.getState().getPreference('connection.smoothHeading');

/**
 * Returns the crab setting for any area or highway segment from getAreaOrSegmentById.
 */
export function getAreaCrabSetting(
  result:
    | { type: 'area'; entity: Exclude<Area, HighwayArea> }
    | { type: 'segment'; entity: Segment }
    | undefined,
): number {
  if (!result) return 0;

  switch (result.type) {
    case 'segment':
      return (
        useFloorplanDesignStore.getState().getHighwayBySegmentIdSilently(result.entity.id)
          ?.highwayPoiSpecs[0]?.crabSetting ?? 0
      );
    case 'area': {
      const { entity } = result;
      switch (entity.areaType) {
        case 'STATIONARY':
          return entity.stationaryPoiSpecs[0]?.crabSetting ?? 0;
        case 'SINGULAR':
          return entity.loadHandlingPoiSpecs[0]?.crabSetting ?? 0;
        case 'RACKING':
          return entity.crabSetting ?? 0;
        case 'MANUAL':
          return 0;
        default:
          return assertNever(entity);
      }
    }
    default:
      return assertNever(result);
  }
}
//
// Area connection category
//
/**
 * Logical category for an area, used in the connection matrix.
 *
 * Connection matrix (upper triangle, areas sorted by category order):
 *
 * From \ To             | HIGHWAY                      | RACKING_INSIDE     | RACKING_ALONGSIDE  | SINGULAR          | STATIONARY        | MANUAL
 * HIGHWAY               | crossing/counter-flow/co-flow| crossing/parallel  | crossing/parallel  | crossing/parallel | crossing/parallel | N/A
 * RACKING_INSIDE        | –                            | distant            | distant            | distant           | distant           | N/A
 * RACKING_ALONGSIDE     | –                            | –                  | distant            | distant           | distant           | N/A
 * SINGULAR              | –                            | –                  | –                  | distant           | distant           | N/A
 * STATIONARY            | –                            | –                  | –                  | –                 | distant           | logical one-way
 * MANUAL                | –                            | –                  | –                  | –                 | –                 | N/A
 */
export type AreaConnectionCategory =
  | 'HIGHWAY'
  | 'RACKING_INSIDE'
  | 'RACKING_ALONGSIDE'
  | 'SINGULAR'
  | 'STATIONARY'
  | 'MANUAL';

/**
 * Classifies a RackingArea's rackType into the relevant connection category.
 */
export const getRackingCategory = (
  rackType: RackingArea['rackType'],
): 'RACKING_INSIDE' | 'RACKING_ALONGSIDE' => {
  switch (rackType) {
    case 'ONE_SIDED_INSIDE':
    case 'SHUTTLE':
      return 'RACKING_INSIDE';
    case 'ONE_SIDED_ALONGSIDE':
    case 'TWO_SIDED_ALONGSIDE':
      return 'RACKING_ALONGSIDE';
    default:
      return assertNever(rackType);
  }
};

/**
 * Resolves the connectNeighbors value for a racking area.
 * Alongside rack types default to curved forward-only when no explicit preference is set.
 */
export const resolveRackingConnectNeighbors = (
  rackType: RackingArea['rackType'],
  preferredConnectNeighbors: RackingArea['connectNeighbors'],
): RackingArea['connectNeighbors'] => {
  const isAlongside = getRackingCategory(rackType) === 'RACKING_ALONGSIDE';
  const hasNoPreference = preferredConnectNeighbors === 'NO_NEIGHBOR_MAPPING';

  if (isAlongside && hasNoPreference) {
    return 'MAP_NEIGHBORS_WITHOUT_PIVOTS_FORWARD_ONLY';
  }

  return preferredConnectNeighbors;
};

//
// Drive direction helper
//
/**
 * Returns the drive direction for DRIVE_IN connections.
 * Areas that connect neighbours drive forward; areas that don't must reverse in.
 */
const getDriveDirection = (connectNeighbors: ConnectNeighbors): AreaConnection['driveDirection'] =>
  connectNeighbors !== 'NO_NEIGHBOR_MAPPING' ? 'FORWARD' : 'REVERSE';

//
// Determines the initial smoothHeading value for a new connection.
//
function resolveInitialSmoothHeading(idA: string, idB: string): boolean {
  const { getAreaOrSegmentById } = useFloorplanDesignStore.getState();
  const hasCrab =
    getAreaCrabSetting(getAreaOrSegmentById(idA)) !== 0 ||
    getAreaCrabSetting(getAreaOrSegmentById(idB)) !== 0;
  return hasCrab || isAnyVehicleOmni();
}

//
// Highway to Highway — crossing (angled segments, ≥ 20° from parallel/anti-parallel)
//
export function buildHighwayToHighwayCrossing(
  segmentIdA: string,
  segmentIdB: string,
): InterAreaConnection {
  const smoothHeading = resolveInitialSmoothHeading(segmentIdA, segmentIdB);
  return {
    id: uuid(),
    idA: segmentIdA,
    idB: segmentIdB,
    aToBConnections: [
      {
        connectionType: 'CROSSING',
        driveDirection: 'FORWARD',
        trackType: 'CURVED',
        smoothHeading,
      },
    ],
    bToAConnections: [
      {
        connectionType: 'CROSSING',
        driveDirection: 'FORWARD',
        trackType: 'CURVED',
        smoothHeading,
      },
    ],
    createdExplicitly: false,
  };
}

//
// Highway to Highway — counter-flow (angle difference ~160–200°)
//
export function buildHighwayToHighwayCounterFlow(
  segmentIdA: string,
  segmentIdB: string,
): InterAreaConnection {
  const smoothHeading = resolveInitialSmoothHeading(segmentIdA, segmentIdB);
  return {
    id: uuid(),
    idA: segmentIdA,
    idB: segmentIdB,
    aToBConnections: [
      {
        connectionType: 'COUNTER_FLOW',
        driveDirection: 'FORWARD',
        trackType: 'CURVED',
        smoothHeading,
      },
    ],
    bToAConnections: [
      {
        connectionType: 'COUNTER_FLOW',
        driveDirection: 'FORWARD',
        trackType: 'CURVED',
        smoothHeading,
      },
    ],
    createdExplicitly: false,
  };
}

//
// Highway to Highway — co-flow (angle difference < 20°)
//
export function buildHighwayToHighwayCoFlow(
  segmentIdA: string,
  segmentIdB: string,
): InterAreaConnection {
  const smoothHeading = resolveInitialSmoothHeading(segmentIdA, segmentIdB);
  return {
    id: uuid(),
    idA: segmentIdA,
    idB: segmentIdB,
    aToBConnections: [
      {
        connectionType: 'CO_FLOW',
        driveDirection: 'FORWARD',
        trackType: 'CURVED',
        smoothHeading,
      },
    ],
    bToAConnections: [
      {
        connectionType: 'CO_FLOW',
        driveDirection: 'FORWARD',
        trackType: 'CURVED',
        smoothHeading,
      },
    ],
    createdExplicitly: false,
  };
}

//
// Explicit distant connection — NEAREST (both directions, created by user)
//
export function buildDistantConnection(idA: string, idB: string): InterAreaConnection {
  const smoothHeading = resolveInitialSmoothHeading(idA, idB);
  return {
    id: uuid(),
    idA,
    idB,
    aToBConnections: [
      {
        connectionType: 'NEAREST',
        driveDirection: 'FORWARD',
        trackType: 'CURVED',
        smoothHeading,
      },
    ],
    bToAConnections: [
      {
        connectionType: 'NEAREST',
        driveDirection: 'FORWARD',
        trackType: 'CURVED',
        smoothHeading,
      },
    ],
    createdExplicitly: true,
  };
}

//
// Highway to RACKING_INSIDE — angled (angle mod 180 ≥ 20°): crossing
//
export function buildHighwayToRackingInsideCrossing(
  areaId: string,
  segmentId: string,
): InterAreaConnection {
  const smoothHeading = resolveInitialSmoothHeading(areaId, segmentId);
  return {
    id: uuid(),
    idA: segmentId,
    idB: areaId,
    aToBConnections: [
      {
        connectionType: 'CROSSING',
        driveDirection: 'FORWARD',
        trackType: 'CURVED',
        smoothHeading,
      },
    ],
    bToAConnections: [
      {
        connectionType: 'CROSSING',
        driveDirection: 'FORWARD',
        trackType: 'CURVED',
        smoothHeading,
      },
    ],
    createdExplicitly: false,
  };
}

//
// Highway to RACKING_INSIDE — parallel (angle mod 180 < 20°): drive in / drive out
//
export function buildHighwayToRackingInsideParallel(
  areaId: string,
  segmentId: string,
  connectNeighbors: ConnectNeighbors,
): InterAreaConnection {
  const smoothHeading = resolveInitialSmoothHeading(areaId, segmentId);
  return {
    id: uuid(),
    idA: segmentId,
    idB: areaId,
    aToBConnections: [
      {
        connectionType: 'DRIVE_IN',
        driveDirection: getDriveDirection(connectNeighbors),
        trackType: 'CURVED',
        smoothHeading,
      },
    ],
    bToAConnections: [
      {
        connectionType: 'DRIVE_OUT',
        driveDirection: 'FORWARD',
        trackType: 'CURVED',
        smoothHeading,
      },
    ],
    createdExplicitly: false,
  };
}

//
// Highway to RACKING_ALONGSIDE / SINGULAR / STATIONARY — parallel: drive in / drive out
//
export function buildHighwayToOtherAreaParallel(
  areaId: string,
  segmentId: string,
  connectNeighbors: ConnectNeighbors,
): InterAreaConnection {
  const smoothHeading = resolveInitialSmoothHeading(areaId, segmentId);
  return {
    id: uuid(),
    idA: segmentId,
    idB: areaId,
    aToBConnections: [
      {
        connectionType: 'DRIVE_IN',
        driveDirection: getDriveDirection(connectNeighbors),
        trackType: 'CURVED',
        smoothHeading,
      },
    ],
    bToAConnections: [
      {
        connectionType: 'DRIVE_OUT',
        driveDirection: 'FORWARD',
        trackType: 'CURVED',
        smoothHeading,
      },
    ],
    createdExplicitly: false,
  };
}

//
// Highway to RACKING_ALONGSIDE / SINGULAR / STATIONARY — angled: crossing
//
export function buildHighwayToOtherAreaCrossing(
  areaId: string,
  segmentId: string,
): InterAreaConnection {
  const smoothHeading = resolveInitialSmoothHeading(areaId, segmentId);
  return {
    id: uuid(),
    idA: segmentId,
    idB: areaId,
    aToBConnections: [
      {
        connectionType: 'CROSSING',
        driveDirection: 'FORWARD',
        trackType: 'CURVED',
        smoothHeading,
      },
    ],
    bToAConnections: [
      {
        connectionType: 'CROSSING',
        driveDirection: 'FORWARD',
        trackType: 'CURVED',
        smoothHeading,
      },
    ],
    createdExplicitly: false,
  };
}

//
// STATIONARY (EXTRACTION) → MANUAL — one-way LOGICAL (extraction drives to manual)
// idA = stationary extraction area, idB = manual area
//
export function buildStationaryExtractionToManual(
  stationaryExtractionId: string,
  manualAreaId: string,
): InterAreaConnection {
  const smoothHeading = resolveInitialSmoothHeading(stationaryExtractionId, manualAreaId);
  return {
    id: uuid(),
    idA: stationaryExtractionId,
    idB: manualAreaId,
    aToBConnections: [
      {
        connectionType: 'LOGICAL',
        driveDirection: 'FORWARD',
        trackType: 'CURVED',
        smoothHeading,
      },
    ],
    bToAConnections: [],
    createdExplicitly: false,
  };
}

//
// MANUAL → STATIONARY (INSERTION) — one-way LOGICAL (manual drives to insertion)
// idA = manual area, idB = stationary insertion area
//
export function buildManualToStationaryInsertion(
  manualAreaId: string,
  stationaryInsertionId: string,
): InterAreaConnection {
  const smoothHeading = resolveInitialSmoothHeading(manualAreaId, stationaryInsertionId);
  return {
    id: uuid(),
    idA: manualAreaId,
    idB: stationaryInsertionId,
    aToBConnections: [
      {
        connectionType: 'LOGICAL',
        driveDirection: 'FORWARD',
        trackType: 'CURVED',
        smoothHeading,
      },
    ],
    bToAConnections: [],
    createdExplicitly: false,
  };
}

//
// Shared factory object — use this throughout the app
//
export const factory = {
  buildHighwayToHighwayCrossing,
  buildHighwayToHighwayCounterFlow,
  buildHighwayToHighwayCoFlow,
  buildDistantConnection,
  buildHighwayToRackingInsideCrossing,
  buildHighwayToRackingInsideParallel,
  buildHighwayToOtherAreaParallel,
  buildHighwayToOtherAreaCrossing,
  buildStationaryExtractionToManual,
  buildManualToStationaryInsertion,
};
